import React, { useState } from "react";
import {
  MdCarCrash,
  MdExpandMore,
  MdExpandLess,
  MdEditRoad,
  MdAutoAwesome,
  MdHelp,
  MdLocationOn,
  MdDateRange,
  MdInfo,
  MdGavel,
  MdWarning,
  MdSpeed,
  MdDownload,
} from "react-icons/md";
import { CSSTransition } from "react-transition-group";
import { useSelector } from "react-redux";
import AcdTooltip from "./AcdTooltip";

const BUSINESS_BASE_URL =
  process.env.REACT_APP_BUSINESS_URL || "https://records.4safedrivers.com";

// Helper function to format text
const formatText = (text) => {
  text = text.toLowerCase();
  return text
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// Define field groups without titles
const FIELD_GROUPS = {
  dates: {
    title: "Dates",
    fields: ["violation-date", "date", "date2", "reinstatement-date"],
  },
  location: {
    title: "Location",
    fields: ["city-location", "court", "state", "county"],
  },
  details: {
    title: "Details",
    fields: ["state-code", "acd", "points", "miscellaneous", "class"],
  },
  metadata: {
    title: "Event Metadata",
    fields: ["description", "event-type", "sub-type"],
  },
};

// Category icons and colors
const getCategoryConfig = (category) => {
  const configs = {
    violation: {
      icon: MdSpeed,
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-200",
      dotColor: "bg-orange-500",
    },
    suspension: {
      icon: MdGavel,
      color: "text-red-600",
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
      dotColor: "bg-red-500",
    },
    accident: {
      icon: MdCarCrash,
      color: "text-yellow-600",
      bgColor: "bg-yellow-50",
      borderColor: "border-yellow-200",
      dotColor: "bg-yellow-500",
    },
    default: {
      icon: MdInfo,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      dotColor: "bg-blue-500",
    },
  };
  return configs[category.toLowerCase()] || configs.default;
};

// This component displays driving violations grouped by category with a smooth accordion effect.
// It allows users to click on record fields to select them and view detailed information.
const Violations = ({
  data,
  driver_id,
  account_id,
  setSelectedKey,
  setSelectedValue,
  onRecordSelect,
}) => {
  const [selectedItem, setSelectedItem] = useState({ key: null, value: null });
  const [openCategory, setOpenCategory] = useState(null);
  const [isHeaderOpen, setIsHeaderOpen] = useState(true);

  // Get token from Redux for download link
  const { token } = useSelector((state) => state.auth);

  // Get order_item_id from data (added by backend /report endpoint)
  const order_item_id = data?.order_item_id;

  if (!data?.driving_record?.["xml-history"]) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <MdEditRoad className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-500 text-sm">No driving history available</p>
        </div>
      </div>
    );
  }

  // Extract driving history and MVR date
  const drivingHistory = data.driving_record?.["xml-history"] || {};
  const mvr_date = data.driving_record?.order?.report_date || null;

  // Group records by category
  const groupedRecords = Object.entries(drivingHistory).reduce(
    (acc, [key, value]) => {
      if (Array.isArray(value)) {
        acc[key] = value;
      }
      return acc;
    },
    {},
  );

  // Helper function to group record fields
  const groupRecordFields = (record) => {
    const groups = {};
    const ungrouped = {};

    // Fields to skip from display (they're handled specially)
    const skipFields = [
      "description",
      "acd_detail",
      "match_type",
      "confidence",
    ];

    // Process each field in the record
    Object.entries(record).forEach(([key, value]) => {
      // Skip special fields
      if (skipFields.includes(key)) return;

      // Check if the field belongs to a group
      let grouped = false;
      for (const [groupName, group] of Object.entries(FIELD_GROUPS)) {
        if (group.fields.some((field) => key.toLowerCase().includes(field))) {
          groups[groupName] = groups[groupName] || {};
          groups[groupName][key] = value;
          grouped = true;
          break;
        }
      }

      // If not in any group, add to ungrouped
      if (!grouped) {
        ungrouped[key] = value;
      }
    });

    return { groups, ungrouped };
  };

  // Updated dot color function to use config
  const getDotColor = (category) => {
    const config = getCategoryConfig(category);
    return config.dotColor;
  };

  // Add this at the top with other imports
  const styles = `
  .accordion-content {
    transition: all 0.5s ease-in-out;
    max-height: 0;
    overflow: hidden;
  }

  .accordion-content.open {
    max-height: 2000px;
  }

  .accordion-rotate {
    transition: transform 0.3s ease;
  }

  .accordion-rotate.open {
    transform: rotate(180deg);
  }
  `;

  // Helper function to deeply stringify any value
  const deepStringifyValue = (val) => {
    if (val === null || val === undefined) return "";
    if (typeof val === "object") {
      // Handle both arrays and objects
      return JSON.stringify(val, null, 2);
    }
    return String(val);
  };

  // Function to handle click on record fields
  const handleClick = (key, value, record, category) => {
    const recordData = {
      key,
      value: deepStringifyValue(value),
      account_id,
      driver_id,
      context: {
        category,
        record: Object.entries(record).reduce((acc, [k, v]) => {
          acc[k] = deepStringifyValue(v);
          return acc;
        }, {}),
      },
    };

    setSelectedKey(key);
    setSelectedValue(deepStringifyValue(value));
    onRecordSelect?.(recordData);
  };

  return (
    <div className="bg-white">
      {/* Add styles */}
      <style>{styles}</style>

      {/* Modern Header */}
      <div className="border-b border-gray-100">
        <button
          onClick={() => setIsHeaderOpen(!isHeaderOpen)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors group"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
              <MdEditRoad className="text-white text-xl" />
            </div>
            <div className="text-left">
              <h2 className="text-lg font-bold text-gray-900">
                Driving History
              </h2>
              <p className="text-sm text-gray-500">
                {Object.keys(groupedRecords).length} categories
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Download MVR Link */}
            {order_item_id && token && (
              <a
                href={`${BUSINESS_BASE_URL}/login?l=all-driver-pdf&oi=${order_item_id}&t=${token}`}
                className="hidden sm:flex items-center gap-2 bg-blue-50 px-3 py-2 rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors"
                title="Download MVR"
              >
                <MdDownload className="text-blue-500 text-lg" />
                <span className="text-xs font-medium text-blue-700">
                  Download MVR
                </span>
              </a>
            )}
            {mvr_date && (
              <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
                <MdDateRange className="text-gray-400 text-sm" />
                <div className="text-right">
                  <p className="text-xs text-gray-500">MVR Date</p>
                  <p className="text-xs font-medium text-gray-700">
                    {new Date(mvr_date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </p>
                </div>
              </div>
            )}
            <MdExpandMore
              className={`text-gray-400 text-xl transition-transform duration-300 ${
                isHeaderOpen ? "rotate-180" : ""
              }`}
            />
          </div>
        </button>
      </div>

      {/* Content Section with smooth transition */}
      <div className={`accordion-content ${isHeaderOpen ? "open" : ""}`}>
        <div className="space-y-4 px-6 pb-6 pt-6">
          {Object.entries(groupedRecords).map(([category, records]) => {
            const isOpen = openCategory === category;
            const config = getCategoryConfig(category);
            const IconComponent = config.icon;

            return (
              <div
                key={category}
                className={`rounded-xl border ${config.borderColor} overflow-hidden bg-white shadow-sm hover:shadow-md transition-all duration-300`}
              >
                {/* Category button */}
                <button
                  className={`w-full px-4 py-3 flex items-center justify-between ${config.bgColor} hover:bg-opacity-80 transition-all duration-300`}
                  onClick={() => setOpenCategory(isOpen ? null : category)}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2 bg-white rounded-lg shadow-sm ${config.color}`}
                    >
                      <IconComponent className="text-lg" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-bold text-gray-900 text-base">
                        {formatText(category)}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {records.length} record{records.length !== 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>
                  <MdExpandMore
                    className={`text-gray-500 text-lg transition-transform duration-300 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {/* Category content with smooth transition */}
                <div className={`accordion-content ${isOpen ? "open" : ""}`}>
                  <div className="relative border-t border-gray-100 bg-gradient-to-b from-white to-blue-50/30">
                    {/* Timeline vertical line */}
                    <div className="absolute left-8 top-0 bottom-0 w-[2px] bg-blue-200" />
                    <div className="divide-y divide-gray-50">
                      {records.map((record, index) => (
                        <div
                          key={index}
                          className="relative p-5 hover:bg-blue-50/50 transition-all duration-200"
                        >
                          {/* Timeline dot */}
                          <div className="absolute left-[44px] top-7">
                            <div
                              className={`w-3 h-3 rounded-full shadow-sm ${getDotColor(
                                category,
                              )} border-2 border-white`}
                            />
                            {/* Vertical connector between items */}
                            {index !== records.length - 1 && (
                              <div className="absolute top-3 left-1 w-[1px] h-full bg-blue-100" />
                            )}
                          </div>

                          {/* Content */}
                          <div className="ml-16">
                            <div className="flex items-center gap-2 mb-3">
                              {record.description && (
                                <div
                                  className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-3 py-1.5 rounded-full cursor-pointer hover:shadow-lg transition-all duration-300 text-sm font-medium"
                                  onClick={() => {
                                    const contextData = {
                                      key: "description",
                                      value: deepStringifyValue(
                                        record.description,
                                      ),
                                      context: {
                                        category,
                                        record: Object.entries(record).reduce(
                                          (acc, [k, v]) => {
                                            acc[k] = deepStringifyValue(v);
                                            return acc;
                                          },
                                          {},
                                        ),
                                      },
                                    };

                                    console.log(
                                      "🎯 Violation clicked!",
                                      contextData,
                                    );

                                    setSelectedKey("description");
                                    setSelectedValue(
                                      deepStringifyValue(record.description),
                                    );
                                    setSelectedItem({
                                      key: "description",
                                      value: deepStringifyValue(
                                        record.description,
                                      ),
                                    });
                                    onRecordSelect?.(contextData);
                                    console.log("✅ onRecordSelect called");
                                  }}
                                >
                                  <MdAutoAwesome className="text-sm" />
                                  <span>{formatText(record.description)}</span>
                                </div>
                              )}

                              {/* Show ACD tooltip if code exists */}
                              {record.acd && (
                                <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 px-2 py-1 rounded-lg">
                                  <span className="text-xs font-semibold text-amber-800">
                                    ACD: {record.acd}
                                  </span>
                                  <AcdTooltip
                                    data={{
                                      acd_detail: {
                                        acd_code:
                                          record.acd_detail?.acd_code ||
                                          record.acd,
                                        acd_description:
                                          record.acd_detail?.acd_description ||
                                          record.description ||
                                          `ACD code ${record.acd}`,
                                        match_type:
                                          record.acd_detail?.match_type ||
                                          "direct",
                                        confidence:
                                          record.acd_detail?.confidence || null,
                                        FMCSR_Category:
                                          record.acd_detail?.FMCSR_Category ||
                                          "--",
                                        FMCSR_Description:
                                          record.acd_detail
                                            ?.FMCSR_Description || "--",
                                        NDR_Category:
                                          record.acd_detail?.NDR_Category ||
                                          "--",
                                        NDR_Description:
                                          record.acd_detail?.NDR_Description ||
                                          "--",
                                      },
                                    }}
                                  />
                                </div>
                              )}
                            </div>

                            {/* Rest of the record fields - keeping your original layout */}
                            <ul className="flex flex-wrap gap-x-6 gap-y-2 font-mono text-[12px]">
                              {Object.values(
                                groupRecordFields(record).groups,
                              ).map((fields) =>
                                Object.entries(fields).map(([key, value]) => (
                                  <li
                                    key={key}
                                    className="flex items-start gap-2 text-gray-700 cursor-pointer hover:bg-blue-100 p-1.5 rounded-md transition-colors"
                                    onClick={() =>
                                      handleClick(key, value, record, category)
                                    }
                                  >
                                    <span className="font-medium text-blue-700 bg-blue-50 rounded px-2 py-0.5 text-xs">
                                      {formatText(key)}:
                                    </span>
                                    <span className="text-gray-800">
                                      {typeof value === "object"
                                        ? JSON.stringify(value)
                                        : String(value)}{" "}
                                      {/* Convert to string explicitly */}
                                    </span>
                                  </li>
                                )),
                              )}

                              {Object.entries(
                                groupRecordFields(record).ungrouped,
                              ).map(([key, value]) => (
                                <li
                                  key={key}
                                  className="flex items-start gap-2 text-gray-700 cursor-pointer hover:bg-blue-100 p-1.5 rounded-md transition-colors"
                                  onClick={() =>
                                    handleClick(key, value, record, category)
                                  }
                                >
                                  <span className="font-medium text-blue-700 bg-blue-50 rounded px-2 py-0.5 text-xs">
                                    {formatText(key)}:
                                  </span>
                                  <span className="text-gray-800">
                                    {typeof value === "object"
                                      ? JSON.stringify(value)
                                      : String(value)}{" "}
                                    {/* Convert to string explicitly */}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Violations;
